import { getObjectHistoryState } from './historyState.js';
import Bowser from 'bowser';

const MARKER = '___IFRAME_DEPTH_BRIDGE___';

const isOurState = (state) =>
  !!state && state[MARKER] === true;

export const shouldDisableHistory = () => {
  // The Android SDK handles navigation natively, so browser history management would conflict with it.
  const isAndroidSdk = !!window.Android;
  const isIOS = Bowser.getParser(window.navigator.userAgent).getOSName() === 'iOS';
  // The embed runs inside an intermediary iframe instead of directly on the top-level merchant page.
  // Combined with iOS, rewinding history can expose a child player navigation entry.
  const isNestedFrame = window.self !== window.top;

  return isAndroidSdk || (isIOS && isNestedFrame);
};

const stateForDepth = (depth) => ({
  ...getObjectHistoryState(history.state),
  [MARKER]: true,
  depth,
});

const restoreOriginalStateIfAtDepth0 = (originalState) => {
  const st = history.state;
  if (isOurState(st) && st.depth === 0) {
    history.replaceState(originalState, '', document.location.href);
    return true;
  }
  return false;
};

// One entry per depth level so one browser Back maps to one iframe back.
const pushToDepth = (fromDepth, toDepth) => {
  for (let d = fromDepth + 1; d <= toDepth; d += 1) {
    history.pushState(stateForDepth(d), '', document.location.href);
  }
};

export class BrowserHistoryManager extends EventTarget {
  static _instance = null;

  static _getInstance = () => {
    if (!BrowserHistoryManager._instance) {
      BrowserHistoryManager._instance = new BrowserHistoryManager();
    }
    return BrowserHistoryManager._instance;
  };

  // --- Static public API (singleton façade) ---

  static setDepth = (nextDepth) => {
    BrowserHistoryManager._getInstance()._setDepth(nextDepth);
  };

  static getDepth = () => BrowserHistoryManager._getInstance()._getDepth();

  static destroy = ({ restoreHistory = true } = {}) => {
    BrowserHistoryManager._getInstance()._destroy({ restoreHistory });
    // Keep the singleton instance around; destroy() makes it inert.
    // If you want re-init semantics later, we can add a reset() method.
  };

  static addEventListener = (type, listener, options) => {
    BrowserHistoryManager._getInstance().addEventListener(type, listener, options);
  };

  static removeEventListener = (type, listener, options) => {
    BrowserHistoryManager._getInstance().removeEventListener(type, listener, options);
  };

  // --- Instance implementation (kept private-by-convention) ---

  constructor() {
    super();

    // Make direct instantiation non-fatal but strongly discouraged.
    if (BrowserHistoryManager._instance) {
      // eslint-disable-next-line no-constructor-return
      return BrowserHistoryManager._instance;
    }

    this._originalState = history.state;
    this._depth = 0;
    this._suppressPop = false;

    this._destroyed = shouldDisableHistory();

    this._setDepthAndDispatch = (nextDepth) => {
      this._depth = nextDepth;
      this.dispatchEvent(
        new CustomEvent('depthChanged', { detail: { depth: this._depth } })
      );
    };

    if (this._destroyed) {
      BrowserHistoryManager._instance = this;
      return;
    }

    this._onPopState = (ev) => {
      if (this._destroyed) return;

      const st = ev.state;

      // If host navigates independently, stop trying to sync.
      if (!isOurState(st)) {
        this._suppressPop = false;
        this._setDepthAndDispatch(0);
        return;
      }

      const newDepth = st.depth;

      // popstate caused by our own history.go()
      if (this._suppressPop) {
        this._suppressPop = false;
        this._setDepthAndDispatch(newDepth);
        return;
      }

      // User pressed browser Back → iframe should go back
      const delta = this._depth - newDepth;
      if (delta > 0) {
        this._setDepthAndDispatch(newDepth);
        this.dispatchEvent(
          new CustomEvent('goBack', { detail: { steps: delta } })
        );
      }
    };

    // Anchor current entry as depth 0 without adding history.
    history.replaceState(stateForDepth(0), '', document.location.href);
    window.addEventListener('popstate', this._onPopState);

    BrowserHistoryManager._instance = this;
  }

  _setDepth(nextDepth) {
    if (this._destroyed) return;

    const d = Number(nextDepth);
    if (!Number.isFinite(d) || d < 0) return;

    // Ignore updates while a rewind is in flight.
    if (this._suppressPop) return;

    // Iframe navigated forward → add synthetic history
    if (d > this._depth) {
      pushToDepth(this._depth, d);
      this._setDepthAndDispatch(d);
      return;
    }

    // Iframe navigated back internally → remove synthetic history
    if (d < this._depth) {
      this._suppressPop = true;
      history.go(-(this._depth - d));
    }
  }

  _getDepth() {
    return this._depth;
  }

  _destroy({ restoreHistory = true } = {}) {
    if (this._destroyed) return;
    this._destroyed = true;

    window.removeEventListener('popstate', this._onPopState);

    if (!restoreHistory) return;

    if (this._depth === 0) {
      restoreOriginalStateIfAtDepth0(this._originalState);
      return;
    }

    // Wait until rewind lands at depth 0 before restoring state.
    const cleanupPopState = () => {
      if (restoreOriginalStateIfAtDepth0(this._originalState)) {
        window.removeEventListener('popstate', cleanupPopState);
      }
    };

    window.addEventListener('popstate', cleanupPopState);

    this._suppressPop = true;
    history.go(-this._depth);
  }
}
